import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  SkeletonText,
  Stack,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";
import ButtonEmailPreview from "components/ButtonEmailPreview";
import CompanyHeader from "components/ui/CompanyHeader";
import DateTime from "components/ui/DateTime";
import HeaderBarInternal from "components/ui/HeaderBarInternal";
import InfoList from "components/ui/InfoList";
import PeriodTime from "components/ui/PeriodTime";
import TagEmailStatus from "components/ui/TagEmailStatus";
import { usePermissionsContext } from "contexts/PermissionsContext";
import { LinkAgendaPreview } from "features/emails/config";
import LogHistory from "features/logs/components/LogHistory";
import EmailSentStats from "features/reports/components/EmailSentStats";
import { useResource } from "hooks/useResource";
import { useLocation, useParams } from "react-router-dom";

export default function PageAgendaDetail() {
  const { id } = useParams();
  const location = useLocation();
  const { ability } = usePermissionsContext();
  const { data: report, isLoading } = useResource({
    id: id as string,
    resourceName: "agenda",
    path: "agendas/send-report",
    queryKey: ["agenda-send-report", id],
  });

  return (
    <>
      <HeaderBarInternal
        title="Visualizar Disparo de Agenda"
        backTo={`/disparos-de-agenda${location.search}`}
      ></HeaderBarInternal>

      <Divider />

      <Container variant="internal" flexDirection="column" mb="80px">
        <SkeletonText isLoaded={!isLoading}>
          {report && (
            <>
              <Card>
                <CardHeader>
                  <Flex alignItems="center">
                    <Stack spacing="5px" alignItems="flex-start" mr="25px">
                      <Heading as="h3" size="h3">
                        {report.title}
                      </Heading>
                      <TagEmailStatus record={report} />
                    </Stack>
                    <ButtonEmailPreview url={LinkAgendaPreview(report)} />
                  </Flex>
                </CardHeader>
                <CardBody>
                  <InfoList
                    mt="10px"
                    ml={0}
                    justifyContent="flex-start"
                    spacing="35px"
                  >
                    <Box>
                      <Text as="span">
                        <small>Criado por</small>
                        {report.createdBy.name}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="span">
                        <small>Modelo de disparo</small>
                        {report.contentGroup.name}
                      </Text>
                    </Box>
                    <Box>
                      <Text as="span">
                        <small>Período referência das notas</small>
                        <PeriodTime
                          startsAt={report.startsAt}
                          endsAt={report.endsAt}
                          noBreakLine
                        />
                      </Text>
                    </Box>
                  </InfoList>

                  <InfoList
                    mt="25px"
                    ml={0}
                    justifyContent="flex-start"
                    spacing="35px"
                  >
                    <Box>
                      <Text as="span">
                        <small>Assunto do e-mail</small>
                        {report.title}
                      </Text>
                    </Box>

                    <Box>
                      <Text as="span">
                        <small>Criado em</small>
                        <DateTime date={report.createdAt} noBreak />
                      </Text>
                    </Box>
                    {report.email && (
                      <Box>
                        <Text as="span">
                          <small>Enviado em</small>
                          <DateTime date={report.email.createdAt} noBreak />
                        </Text>
                      </Box>
                    )}
                  </InfoList>

                  {report.email && (
                    <InfoList
                      mt="25px"
                      ml={0}
                      justifyContent="flex-start"
                      spacing="35px"
                    >
                      <Box>
                        <Text as="span">
                          <small>Destinatários</small>
                          {report.email.countRecipients}
                        </Text>
                      </Box>
                      <Box>
                        <Text as="span">
                          <small>Enviados</small>
                          {report.email.countSent}
                        </Text>
                      </Box>
                      <Box>
                        <Text as="span">
                          <small>Bounce</small>
                          {report.email.countBounce}
                        </Text>
                      </Box>
                    </InfoList>
                  )}
                </CardBody>
              </Card>

              <Card mt="30px">
                <CardHeader>
                  <Heading as="h3" size="h3">
                    Lista
                    <Text as="span"></Text>
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Stack spacing="50px">
                    <Box>
                      <CompanyHeader
                        company={report.list.company}
                        description=""
                      />
                      <Table mt="23px">
                        <Tbody>
                          <Tr key={report.list.id}>
                            <Td>{report.list.name}</Td>
                            <Td valign="center">
                              {report.list.isNewsletter && (
                                <Tag colorScheme="green">Newsletter</Tag>
                              )}
                            </Td>
                            <Td w="600px">
                              <HStack spacing="8px" flexWrap="wrap">
                                {report.list.tags.map((tag) => (
                                  <Tag
                                    key={`tag-${tag.id}`}
                                    colorScheme={tag.colorScheme}
                                  >
                                    {tag.name}
                                  </Tag>
                                ))}
                              </HStack>
                            </Td>
                          </Tr>
                        </Tbody>
                      </Table>
                    </Box>
                  </Stack>
                </CardBody>
              </Card>

              {report.email && (
                <Card mt="30px">
                  <CardHeader>
                    <Heading as="h3" size="h3">
                      Estatísticas de envio
                    </Heading>
                  </CardHeader>

                  <CardBody>
                    <EmailSentStats email={report.email} />
                  </CardBody>
                </Card>
              )}

              {ability.can("log", "Agenda") && (
                <LogHistory model="AGENDA" modelId={report.id} />
              )}
            </>
          )}
        </SkeletonText>
      </Container>
    </>
  );
}
